import { Injectable, Logger } from '@nestjs/common';
import { Prisma } from '@prisma/client';
import { PrismaService } from '@cart/api-data-access';
import {
  DEFAULT_ORGANIZATION_ID,
  DEFAULT_STORE_ID,
  StoreContextService,
} from '../tenancy/store-context.service';
import { AuditContextService } from './audit-context.service';
import { AuditWriterService } from './audit-writer.service';
import { AuditTriggerRegistry } from './audit-trigger-registry.service';
import { capPayload } from './audit-sampling';

const SENSITIVE_KEY_PATTERNS = [
  /password/i,
  /token/i,
  /authorization/i,
  /cookie/i,
  /secret/i,
  /card/i,
  /cvv/i,
];

export interface InterfaceLogInput {
  correlationId: string;
  method: string;
  path: string;
  routePattern?: string;
  direction?: 'inbound' | 'outbound';
  statusCode?: number;
  requestHeaders?: unknown;
  requestPayload?: unknown;
  responsePayload?: unknown;
  errorPayload?: unknown;
  userId?: string;
  ipAddress?: string;
  userAgent?: string;
  durationMs?: number;
  requestedOn?: Date;
  respondedOn?: Date;
  batchId?: string;
  truncated?: boolean;
  sampleRate?: number;
}

export interface ChangeLogInput {
  schemaName: string;
  tableName: string;
  recordId: string;
  operation: 'create' | 'update' | 'delete' | string;
  changedColumns?: string[];
  oldValue?: unknown;
  newValue?: unknown;
  changedBy?: string;
  correlationId?: string;
  source?: string;
  remarks?: string;
}

/**
 * Distinguishes "not a primitive-like object" from a value that legitimately
 * converted to `undefined` or `null`.
 */
const NOT_PRIMITIVE = Symbol('not-primitive');

@Injectable()
export class AuditService {
  private readonly logger = new Logger(AuditService.name);

  constructor(
    private readonly prisma: PrismaService,
    private readonly storeContext?: StoreContextService,
    private readonly auditContext?: AuditContextService,
    private readonly writer?: AuditWriterService,
    private readonly registry?: AuditTriggerRegistry
  ) {}

  maskPayload<T>(payload: T): T {
    return this.maskValue(payload) as T;
  }

  logInterface(input: InterfaceLogInput): void {
    const row = {
      id: crypto.randomUUID(),
      organizationId: this.organizationId(),
      storeId: this.storeId(),
      correlationId: input.correlationId,
      batchId: input.batchId ?? this.auditContext?.batchId() ?? null,
      direction: input.direction ?? 'inbound',
      method: input.method,
      path: input.path,
      routePattern: input.routePattern ?? null,
      statusCode: input.statusCode ?? null,
      requestHeaders: this.toJson(input.requestHeaders) ?? Prisma.DbNull,
      requestPayload: this.toJson(input.requestPayload) ?? Prisma.DbNull,
      responsePayload: this.toJson(input.responsePayload) ?? Prisma.DbNull,
      errorPayload: this.toJson(input.errorPayload) ?? Prisma.DbNull,
      truncated: input.truncated ?? false,
      sampleRate: input.sampleRate ?? 1,
      userId: input.userId ?? null,
      ipAddress: input.ipAddress ?? null,
      userAgent: input.userAgent ?? null,
      durationMs: input.durationMs ?? null,
      requestedOn: input.requestedOn ?? new Date(),
      respondedOn: input.respondedOn ?? null,
    };

    // Queued, never awaited. The previous version issued an individual insert
    // per request on the app's own connection pool.
    if (this.writer) {
      this.writer.enqueue('interfaceLog', row);
      return;
    }

    void this.prisma.interfaceLog
      .create({ data: row as never })
      .catch((error: unknown) =>
        this.logger.warn(`interface audit write failed: ${this.errorMessage(error)}`)
      );
  }

  /**
   * Records a change the database triggers do not already cover.
   *
   * Since prisma/migrations/manual/045, every Tier-1 and Tier-2 table writes its
   * own change rows from `audit.fn_log_change()`, with the complete before and
   * after state — which the 135 hand-written call sites never captured (2 of
   * them passed oldValue; none passed a correlation id).
   *
   * So for a trigger-covered table this no longer writes a duplicate detail
   * row. What it does instead is more useful: it labels the current batch with
   * the caller's intent, which is what turns "9 rows changed" into "Updated
   * product Blue Kurta" in the admin's collapsed row. Call sites for tables not
   * yet enrolled still write a row the old way, so coverage never goes
   * backwards while enrolment is still being widened.
   */
  async logChange(input: ChangeLogInput): Promise<void> {
    this.describeBatch(input);

    if (this.registry?.isCovered(input.schemaName, input.tableName)) return;

    const row = {
      id: crypto.randomUUID(),
      organizationId: this.organizationId(),
      storeId: this.storeId(),
      schemaName: input.schemaName,
      tableName: input.tableName,
      recordId: input.recordId,
      operation: input.operation,
      changedColumns: this.toJson(input.changedColumns) ?? Prisma.DbNull,
      oldValue: this.toJson(input.oldValue) ?? Prisma.DbNull,
      newValue: this.toJson(input.newValue) ?? Prisma.DbNull,
      changedBy: input.changedBy ?? this.auditContext?.current()?.actorId ?? 'system',
      changedOn: new Date(),
      // Was always NULL before: no call site ever passed one, so nothing linked
      // a database change to the request that caused it.
      correlationId: input.correlationId ?? this.auditContext?.correlationId() ?? null,
      batchId: this.auditContext?.batchId() ?? null,
      actorRole: this.auditContext?.current()?.actorRole ?? null,
      actorName: this.auditContext?.current()?.actorName ?? null,
      ipAddress: this.auditContext?.current()?.ipAddress ?? null,
      source: input.source ?? null,
      remarks: input.remarks ?? null,
    };

    if (this.writer) {
      this.writer.enqueue('changeLog', row);
      return;
    }

    try {
      await this.prisma.changeLog.create({ data: row as never });
    } catch (error) {
      this.logger.warn(`change audit write failed: ${this.errorMessage(error)}`);
    }
  }

  /**
   * Derives a batch label from a call site that already says what it is doing,
   * e.g. source "AddressesService.create" on table "addresses" becomes
   * "address.create" / "Created address".
   */
  private describeBatch(input: ChangeLogInput): void {
    if (!this.auditContext?.current()) return;

    const entity = this.singularise(input.tableName);
    const verb =
      input.operation === 'create' ? 'Created' :
      input.operation === 'delete' ? 'Deleted' : 'Updated';

    this.auditContext.describe(
      `${entity}.${input.operation}`,
      `${verb} ${entity.replace(/_/g, ' ')}`
    );
  }

  private singularise(table: string): string {
    if (table.endsWith('ies')) return `${table.slice(0, -3)}y`;
    if (table.endsWith('ses')) return table.slice(0, -2);
    if (table.endsWith('s')) return table.slice(0, -1);
    return table;
  }

  /** Returns `unknown` rather than Prisma.InputJsonValue on purpose — see AuditWriterService.flush. */
  private toJson(value: unknown): unknown {
    if (value === undefined) return undefined;

    const masked = this.maskPayload(value);
    const { value: capped } = capPayload(masked);

    return capped;
  }

  private maskValue(value: unknown): unknown {
    if (Array.isArray(value)) {
      return value.map((item) => this.maskValue(item));
    }

    if (!value || typeof value !== 'object') {
      // BigInt survives nowhere in JSON, and JSON.stringify throws on it
      // rather than dropping it — so it is turned into a string here.
      return typeof value === 'bigint' ? value.toString() : value;
    }

    const primitive = this.toPrimitive(value);
    if (primitive !== NOT_PRIMITIVE) return primitive;

    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, child]) => [
        key,
        this.isSensitiveKey(key) ? '[REDACTED]' : this.maskValue(child),
      ])
    );
  }

  /**
   * Values that are objects but are not *records*, converted before the walk
   * below can flatten them.
   *
   * `Object.entries` on a Date returns nothing, so every timestamp in an audit
   * payload was being written as `{}` — the log recorded that a change
   * happened and lost when. A Prisma `Decimal` is worse: its own enumerable
   * fields include a function, which Prisma refuses to serialise, so the entire
   * audit row was dropped and the request went unrecorded. Both were silent,
   * because the write is wrapped in a warn-and-continue.
   */
  private toPrimitive(value: object): unknown {
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value.toISOString();
    }

    // Before the toJSON check below, deliberately: Buffer HAS a toJSON, and it
    // expands to a full array of byte values — which is the opposite of what an
    // audit trail wants and can be megabytes of it.
    if (typeof Buffer !== 'undefined' && Buffer.isBuffer(value)) {
      return `[Buffer ${value.length} bytes]`;
    }

    // Prisma Decimal, and anything else that knows how to render itself.
    // Checked structurally rather than by importing Decimal, so this keeps
    // working across Prisma versions and for any other such wrapper.
    const candidate = value as { toJSON?: () => unknown; toFixed?: () => string };
    if (typeof candidate.toJSON === 'function') return candidate.toJSON();
    if (typeof candidate.toFixed === 'function') return candidate.toFixed();

    return NOT_PRIMITIVE;
  }

  private isSensitiveKey(key: string): boolean {
    return SENSITIVE_KEY_PATTERNS.some((pattern) => pattern.test(key));
  }

  private organizationId(): string {
    return this.storeContext?.organizationId() ?? DEFAULT_ORGANIZATION_ID;
  }

  private storeId(): string {
    return this.storeContext?.storeId() ?? DEFAULT_STORE_ID;
  }

  private errorMessage(error: unknown): string {
    return error instanceof Error ? error.message : String(error);
  }
}
